import {
  Card,
  Dropdown,
  Table,
  Space,
  Flex,
  Tag,
  Typography,
  Spin
} from "antd";
import { insertFax } from "api/services/FaxService";
import { getJobs } from "api/services/JobService";
import {
  ApprovedStatus,
  TJob,
  TJobDetails,
  TJobFilter
} from "api/services/models/types";
import { WebDPrivilege } from "api/services/models/WebDUser";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import dayjs from "dayjs";
import { showErrorToast } from "helpers/utils";
import { ReactComponent as ArrowDownIcon } from "Icons/vuesax/arrow-down.svg";
import { ReactComponent as EditIcon } from "Icons/vuesax/edit-2.svg";
import { ReactComponent as ApprovedIcon } from "Icons/vuesax/tick-circle.svg";
import { ReactComponent as DownloadIcon } from "Icons/vuesax/import-2.svg";
import { ReactComponent as MoreIcon } from "Icons/vuesax/more.svg";
import { ReactComponent as PrintIcon } from "Icons/vuesax/printer.svg";
import { ReactComponent as FaxIcon } from "Icons/vuesax/simcard.svg";
import { ReactComponent as TimerIcon } from "Icons/vuesax/timer-1.svg";
import { useEffect, useRef, useState } from "react";
import DropdownFilter from "../DropdownFilter";
import DocumentEditorCard from "./DocumentEditor";
import DownloadModal from "./DownloadManager";
import PrintModal from "./PrintManager";
import NewFaxModal from "./SendFax";
import HistoryModal from "./ViewHistory";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import { useLayoutContext } from "components/MainContainer";
import config  from "../../account-config.json";

interface JobGridProps {
  preparedFilters: any;
  source: string;
  key: number;
}

const JobGrid: React.FC<JobGridProps> = ({ preparedFilters, source, key }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState<boolean>(false);
  const [isPrintModalVisible, setIsPrintModalVisible] = useState<boolean>(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState<boolean>(false);
  const [isFaxModalVisible, setIsFaxModalVisible] = useState<boolean>(false);
  const [selectedRecords, setSelectedRecords] = useState<any[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<TJob | null>();
  const [jobDetails, setJobDetails] = useState<TJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [selectedReportTypes, setSelectedReportTypes] = useState<string[]>([]);
  const [refreshPage, setRefreshPage] = useState<number>(0);
  const { documentTypes, locations, providers } = usePicklists();
  const [uniqueProviders, setUniqueProviders] = useState<string[]>([]);
  const [uniqueReportTypes, setUniqueReportTypes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [downloadButtonDisabled, setDownloadButtonDisabled] = useState<boolean>(false);
  const [printButtonDisabled, setPrintButtonDisabled] = useState<boolean>(false);
  const [faxButtonDisabled, setFaxButtonDisabled] = useState<boolean>(false);
  const [bulkActionButtonDisabled, setBulkActionButtonDisabled] = useState<boolean>(false);
  const { accountId, webDUser } = useTDAuthentication();
  const { toggleDrawer, isSmallScreen } = useLayoutContext();
  const printDisabledAccount =  !!accountId && config["printDisabled"].includes(accountId);
    const downloadDisabledAccount =  !!accountId && config["downloadDisabled"].includes(accountId);
    const faxDisabledAccount =  !!accountId && config["faxDisabled"].includes(accountId);

  useEffect(() => {
    setSelectedRowKeys([]);
    setSelectedProviders([]);
    setSelectedReportTypes([]);
    setUniqueProviders([]);
    setUniqueReportTypes([]);
    setDownloadButtonDisabled(isDownloadDisabled(source) || downloadDisabledAccount);
    setPrintButtonDisabled(isPrintDisabled(source) || printDisabledAccount);
    setFaxButtonDisabled(!(!!webDUser?.hasPrivilege(WebDPrivilege.DocumentsFax)) || faxDisabledAccount);
    setBulkActionButtonDisabled(downloadDisabledAccount && printDisabledAccount && faxDisabledAccount);
    

    const fetchJobs = async () => {
      setIsLoading(true);
      if (!preparedFilters) return;
      try {
        const jobFilter: TJobFilter = {
          jobStatus: preparedFilters.jobStatus,
          patientName: preparedFilters.strPatientName || null,
          mrn: preparedFilters.strMRN || null,
          providerIds: preparedFilters.strProviderId || null,
          reportTypes: preparedFilters.strReportType || null,
          dosFrom: preparedFilters.dtDOSFrom
            ? dayjs(preparedFilters.dtDOSFrom).toISOString()
            : null,
          dosTo: preparedFilters.dtDOSTo ? dayjs(preparedFilters.dtDOSTo).add(1, 'day').startOf('day').toISOString() : null,
          dodFrom: preparedFilters.dtDODFrom
            ? dayjs(preparedFilters.dtDODFrom).toISOString()
            : null,
          dodTo: preparedFilters.dtDODTo ? dayjs(preparedFilters.dtDODTo).add(1, 'day').startOf('day').toISOString() : null,
          jobApprovalStatus: preparedFilters.jobApprovalStatus,
          regions: preparedFilters.strRegion || null,
          locations: preparedFilters.strLocation || null,
          patientDOB: preparedFilters.dtPatDOB
            ? dayjs(preparedFilters.dtPatDOB).toISOString()
            : null,
          vtJobId: preparedFilters.VTJobId || 0,
          qcHoldFilter: preparedFilters.qcholdFilter
        };
        if (
          (source === "QCHold" && !webDUser?.hasPrivilege(WebDPrivilege.ViewQCHold)) ||
          (source === "Inbox" && !webDUser?.hasPrivilege(WebDPrivilege.ViewInbox)) ||
          (source === "Archive" && !webDUser?.hasPrivilege(WebDPrivilege.ViewArchive))
        ) {
          showErrorToast("Permission Denied");
          return;
        }
        const details = await getJobs(jobFilter, accountId || "");
        setJobDetails(details);
      } catch (err: any) {
        console.error("Error fetching job details:", err);
        setError(err.message);
      }
      setIsLoading(false);
    };
    fetchJobs();
  }, [preparedFilters, accountId, key, refreshPage]);

  const handleClickOutside = (event: any) => {
    //@ts-ignore
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (jobDetails.length > 0) {
      const providersSet = new Set(jobDetails.map(job => job.providerName));
      const reportTypesSet = new Set(jobDetails.map(job => job.reportType));

      setUniqueProviders([...providersSet]);
      setUniqueReportTypes([...reportTypesSet]);
    }
  }, [jobDetails]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (
      isModalVisible ||
      isDownloadModalVisible ||
      isPrintModalVisible ||
      isFaxModalVisible ||
      isHistoryModalVisible
    ) {
      document.body.classList.add("blur");
    } else {
      document.body.classList.remove("blur");
    }

    return () => {
      document.body.classList.remove("blur");
    };
  }, [
    isModalVisible,
    isDownloadModalVisible,
    isPrintModalVisible,
    isFaxModalVisible,
    isHistoryModalVisible
  ]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", !isSmallScreen);
  }, [isSmallScreen]);

  const filteredJobDetails = jobDetails.filter(
    job =>
      (selectedProviders.length === 0 || selectedProviders.includes(job.providerName)) &&
      (selectedReportTypes.length === 0 || selectedReportTypes.includes(job.reportType))
    ).sort(
      (a, b) => new Date(b.dateOfDictation).getTime() - new Date(a.dateOfDictation).getTime()
    );

  const handleOpenDownloadModal = () => {
    if (selectedRowKeys.length == 0) {
      showErrorToast("Please select a job");
      alert("Please select a job");
      return;
    }
    setIsDownloadModalVisible(true);
  };

  const handleCloseDownloadModal = () => {
    setRefreshPage(prev => prev + 1);
    setIsDownloadModalVisible(false);
  };

  const handleOpenPrintModal = () => {
    if (selectedRowKeys.length == 0) {
      showErrorToast("Please select a job");
      alert("Please select a job");
      return;
    }
    setIsPrintModalVisible(true);
  };

  const handleOpenFaxModal = () => {
    if (selectedRowKeys.length == 0) {
      showErrorToast("Please select a job");
      alert("Please select a job");
      return;
    }
    setIsFaxModalVisible(true);
  };

  const handleClosePrintModal = () => {
    setIsPrintModalVisible(false);
  };

  const handleHistoryClick = (record: any) => {
    setSelectedRowData(record);
    setIsHistoryModalVisible(true);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalVisible(false);
  };

  const handleFaxClick = (record: any) => {
    setSelectedRowData(record);
    setIsFaxModalVisible(true);
  };

  const handleCloseFaxModal = () => {
    setIsFaxModalVisible(false);
  };

  const handleFaxSubmit = async (values: any) => {
    await insertFax(values, accountId || "");
    alert("Form submitted successfully!");
  };

  const isPrintDisabled = (source: string) => {
    switch (source) {
      case "Inbox":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsPrintInbox);
      case "QCHold":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsPrintQCHold);
      case "Archive":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsPrintArchive);
      default:
        return true;
    }
  };

  const isEditDisabled = (source: string) => {
    switch (source) {
      case "Inbox":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsViewInbox);
      case "QCHold":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsViewQCHold);
      case "Archive":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsViewArchive);
      default:
        return true;
    }
  };

  const isDownloadDisabled = (source: string) => {
    switch (source) {
      case "Inbox":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsDownloadInbox);
      case "QCHold":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsDownloadQCHold);
      case "Archive":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsDownloadArchive);
      default:
        return true;
    }
  };

  const getDropdownItems = (record: any) => {
    const printDisabled = isPrintDisabled(source) || printDisabledAccount;
    return [
      {
        key: "1",
        label: (
          <span
            style={{ display: "flex", alignItems: "center" }}
            onClick={() => handlePrintClick(record)}>
            <PrintIcon
              width="16px"
              height="16px"
              style={{ color: "#1868db", marginRight: "8px" }}
            />
            Print
          </span>
        ),
        disabled: printDisabled
      },
      {
        key: "2",
        label: (
          <span
            style={{ display: "flex", alignItems: "center" }}
            onClick={() => handleHistoryClick(record)}>
            <TimerIcon
              width="16px"
              height="16px"
              style={{ color: "#1868db", marginRight: "8px" }}
            />
            History
          </span>
        ),
        disabled: !webDUser?.hasPrivilege(WebDPrivilege.DocumentsViewHistory)
      }
    ];
  };

  const handleSelectAll = (e: any) => {
    if (e.target.checked) {
      const allKeys = jobDetails.map(job => `${job.jobId}`);
      setSelectedRowKeys(allKeys);
      setSelectedRecords(jobDetails);
    } else {
      setSelectedRowKeys([]);
      setSelectedRecords([]);
    }
  };

  const getTag = (record: TJob) => {
    const approvedStatus = record.approved;
    const isLocked = record.isLocked;
    const printDownloadStatus = record.printDownloadStatus;

    let chipColor = "default";
    let chipBorderColor = "default";
    let chipBGColor = "default";
    let chipText = "";

    if (
      [
        ApprovedStatus.Yes,
        ApprovedStatus.AutoApprovedAndUploaded,
        ApprovedStatus.ProviderApprovedAndUploaded
      ].includes(approvedStatus)
    ) {
      chipBGColor = "#00BE1E33";
      chipBorderColor = "#00BE1E4D";
      chipColor = "#00BE1E";
      chipText = "Approved";
    } else if (isLocked) {
      chipBGColor = "#CAA9FF33";
      chipBorderColor = "#CAA9FF4D";
      chipColor = "#8f6bc9";
      chipText = "Under Review";
    } else {
      switch (printDownloadStatus) {
        case "N":
        case "n":
          chipBGColor = "#77777733";
          chipBorderColor = "#7777774D";
          chipColor = "#777777";
          chipText = "Review";
          break;
        case "H":
        case "h":
          if (record.notesFromClient && record.notesFromClient.trim()) {
            chipBGColor = "#CAA9FF33";
            chipBorderColor = "#CAA9FF4D";
            chipColor = "#926ecc";
            chipText = "Hold (Reviewed)";
          } else {
            chipBGColor = "#FF7A0033";
            chipBorderColor = "#FF7A004D";
            chipColor = "#FF7A00";
            chipText = "Hold (Pending)";
          }
          break;
        default:
          chipBGColor = "#76D6FF33";
          chipBorderColor = "#76D6FF4D";
          chipColor = "#4dadd6";
          chipText = "New";
      }
    }

    return (
      <Tag
        color={chipBGColor}
        style={{
          borderRadius: ".5rem",
          border: `1px solid ${chipBorderColor}`,
          padding: ".25rem .625rem",
          color: `${chipColor}`
        }}>
        {chipText}
      </Tag>
    );
  };

  const columns = [
    {
      title: (
        <input
          type="checkbox"
          checked={selectedRowKeys.length === jobDetails.length && jobDetails.length > 0}
          onChange={handleSelectAll}
        />
      ),
      dataIndex: "select",
      render: (text: string, record: any) => (
        <input
          type="checkbox"
          checked={selectedRowKeys.includes(`${record.jobId}`)}
          onChange={() => handleRowSelect(`${record.jobId}`)}
        />
      )
    },
    {
      title: "Date of Job",
      dataIndex: "dateOfDictation",
      render: (text: string) => dayjs(text).format("MM/DD/YYYY"),
      sorter: (a: any, b: any) =>
        new Date(a.dateOfDictation).valueOf() - new Date(b.dateOfDictation).valueOf()
    },
    {
      title: "Date of Service",
      dataIndex: "visitDate",
      render: (text: string) => dayjs(text).format("MM/DD/YYYY"),
      sorter: (a: any, b: any) =>
        new Date(a.visitDate).valueOf() - new Date(b.visitDate).valueOf()
    },
    {
      title: "MRN",
      dataIndex: "mrn",
      sorter: (a: any, b: any) => a.mrn - b.mrn
    },
    {
      title: "Name",
      dataIndex: "patientName",
      sorter: (a: any, b: any) => a.patientName.localeCompare(b.patientName)
    },
    {
      title: "Provider",
      dataIndex: "providerName",
      sorter: (a: any, b: any) => a.providerName.localeCompare(b.providerName)
    },
    {
      title: "Location",
      dataIndex: "locationShortName",
      sorter: (a: any, b: any) => a.locationShortName.localeCompare(b.locationShortName)
    },
    {
      title: "Report Type",
      dataIndex: "reportType",
      sorter: (a: any, b: any) => a.reportType.localeCompare(b.reportType)
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string, record: TJob) => getTag(record)
    },
    {
      title: "",
      render: (_: any, record: any) => (
        <div style={{ display: "flex" }}>
          {record.approved !== ApprovedStatus.Yes && (
            <EditIcon
              width="16px"
              height="16px"
              style={{
                marginRight: "12px",
                cursor: !record.isLocked && !isEditDisabled(source) ? "pointer" : "not-allowed",
                color: !record.isLocked ? "#1868db" : "#B0B0B0"
              }}
              onClick={() => {
                if (!record.isLocked && !isEditDisabled(source)) {
                  handleEditClick(record);
                }
              }}
            />
          )}
          {record.approved === ApprovedStatus.Yes && (
            <ApprovedIcon
              width="16px"
              height="16px"
              style={{
                marginRight: "12px",
                cursor:
                  !record.isLocked && !isDownloadDisabled(source) ? "pointer" : "not-allowed",
                color: !record.isLocked ? "#0B6623" : "#B0B0B0"
              }}
              onClick={() => {
                if (!record.isLocked && !isDownloadDisabled(source)) {
                  handleEditClick(record);
                }
              }}
            />
          )}
          <DownloadIcon
            width="16px"
            height="16px"
            style={{
              marginRight: "12px",
              cursor: !isDownloadDisabled(source) ? "pointer" : "not-allowed",
              color: "#1868db"
            }}
            onClick={() => {
              if (!isDownloadDisabled(source)) {
                handleDownloadClick(record);
              }
            }}
          />
          <Dropdown
            menu={{
              items: getDropdownItems(record)
            }}
            trigger={["click"]}>
            <MoreIcon
              width="16px"
              height="16px"
              fill="#777777"
              style={{
                marginRight: "12px",
                cursor: "pointer",
                color: "#777777"
              }}
            />
          </Dropdown>
        </div>
      )
    }
  ];

  const handleCheckboxChange = (key: number) => {
    const newSelectedRowKeys = selectedRowKeys.includes(`${key}`)
      ? selectedRowKeys.filter(k => k !== `${key}`)
      : [...selectedRowKeys, `${key}`];
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleEditClick = (record: any) => {
    setSelectedRowData(record);
    setIsModalVisible(true);
  };

  const handleDownloadClick = (record: any) => {
    setSelectedRowKeys([`${record?.jobId}`]);
    setSelectedRecords([record]);
    setIsDownloadModalVisible(true);
  };

  const handlePrintClick = (record: any) => {
    setSelectedRowKeys([`${record?.jobId}`]);
    setSelectedRecords([record]);
    setIsPrintModalVisible(true);
  };

  const handleModalClose = (
    jobId: number | null,
    lock: boolean,
    jobApproved: boolean = false,
    jobDetail: TJobDetails | null
  ) => {
    console.log(jobDetail);
    if (source == "QCHold" && jobApproved) {
      const newData = jobId ? jobDetails.filter(job => job.jobId !== jobId) : jobDetails;
      setJobDetails(newData);
      setIsModalVisible(false);
      setSelectedRowData(null);
    } else {
      const newData = jobId
        ? jobDetails.map(job =>
            job.jobId === jobId
              ? {
                  ...job,
                  isLocked: lock,
                  mrn: jobDetail?.mrn ?? "",
                  patientName: `${jobDetail?.lastName ? jobDetail.lastName + "," : ""} ${
                    jobDetail?.firstName ?? ""
                  } ${jobDetail?.middleInitial ?? ""}`,
                  locationShortName:
                    locations?.find(x => x.locationId === jobDetail?.locationId)
                      ?.locationName ?? "",
                  reportType: jobDetail?.reportType ?? "",
                  visitDate: dayjs(jobDetail?.dateOfService, "MM-DD-YYYY").toDate(),
                  approved: jobApproved
                    ? ApprovedStatus.Yes
                    : jobDetail?.approved ?? ApprovedStatus.No
                }
              : job
          )
        : jobDetails;
      setJobDetails(newData);
      setIsModalVisible(false);
      setSelectedRowData(null);
    }
  };

  const handleRowSelect = (key: string) => {
    const newSelectedKeys = selectedRowKeys.includes(key)
      ? selectedRowKeys.filter(k => k !== key)
      : [...selectedRowKeys, key];
    setSelectedRowKeys(newSelectedKeys);
    const newSelectedRecords = jobDetails.filter(job =>
      newSelectedKeys.includes(`${job.jobId}`)
    );
    setSelectedRecords(newSelectedRecords);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: string[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    }
  };

  return (
    <>
      {!isSmallScreen ? (
        <>
          {(isModalVisible ||
            isDownloadModalVisible ||
            isPrintModalVisible ||
            isFaxModalVisible ||
            isHistoryModalVisible) && <div className="blur" />}

          <Flex wrap gap="middle" justify="space-between">
            <Flex wrap gap="middle" justify="space-between">
              <DropdownFilter
                options={uniqueProviders}
                selectedOptions={selectedProviders}
                setSelectedOptions={setSelectedProviders}
                label="Provider"
              />
              <DropdownFilter
                options={uniqueReportTypes}
                selectedOptions={selectedReportTypes}
                setSelectedOptions={setSelectedReportTypes}
                label="Report Type"
              />
            </Flex>

            <div ref={dropdownRef}>
              <button className="button-style" 
              onClick={!bulkActionButtonDisabled ? toggleDropdown : undefined}
                style={{
                  display: (bulkActionButtonDisabled) ? "none" : "inline-block",
                  cursor: (bulkActionButtonDisabled) ? "not-allowed" : "pointer",
                  opacity: (bulkActionButtonDisabled) ? 0.5 : 1,
                  color: (bulkActionButtonDisabled) ? "#aaa" : "inherit",
                  fontWeight: "500",
                }}
              >
                Bulk Actions{" "}
                <ArrowDownIcon height="10px" width="10px" style={{ marginLeft: "4px" }} />
              </button>
              {isOpen && (
                <div className="dropdown-style">
                  <div
                    onClick={!downloadButtonDisabled ? handleOpenDownloadModal : undefined}
                    className="dropdown-item"
                    style={{ 
                      cursor: (downloadButtonDisabled) ? "not-allowed" : "pointer",
                      opacity: (downloadButtonDisabled) ? 0.5 : 1,
                      color: (downloadButtonDisabled) ? "#aaa" : "inherit"
                      }}>
                    <DownloadIcon width="16px" height="16px" className="download-icon" />
                    Download
                  </div>
                  <div
                    onClick={!printButtonDisabled ? handleOpenPrintModal : undefined}
                    className="dropdown-item"
                    style={{ 
                      cursor: printButtonDisabled ? "not-allowed" : "pointer",
                      opacity: printButtonDisabled ? 0.5 : 1,
                      color: printButtonDisabled ? "#aaa" : "inherit"
                      }}>
                    <PrintIcon width="16px" height="16px" className="print-icon" />
                    Print
                  </div>
                  <div
                    onClick={
                      !faxButtonDisabled
                        ? handleOpenFaxModal
                        : undefined
                    }
                    className="dropdown-item"
                    style={{
                      cursor: faxButtonDisabled ? "not-allowed" : "pointer",
                      opacity: faxButtonDisabled ? 0.5 : 1,
                      color: faxButtonDisabled ? "#aaa" : "inherit"
                    }}>
                    <FaxIcon width="16px" height="16px" className="fax-icon" />
                    Fax
                  </div>
                </div>
              )}
            </div>
          </Flex>

          <Card className="card-style" styles={{ body: { padding: "0px" } }}>
            <Table
              columns={columns}
              loading={isLoading}
              style={{ border: "none" }}
              dataSource={filteredJobDetails}
              pagination={{
                pageSize: pageSize,
                showSizeChanger: {
                  showSearch: false
                },
                pageSizeOptions: [5, 10, 20, 50, 100],
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size || 10);
                }
              }}
              bordered={false}
              className="table-style"
              components={{
                body: {
                  cell: (props: any) => (
                    <td
                      {...props}
                      style={{
                        borderBottomWidth: "1px",
                        borderBottomColor: "#f0f0f0",
                        borderBottomStyle: "solid",
                        fontWeight: "500",
                      }}
                    />
                  )
                }
              }}
            />
          </Card>

          <DocumentEditorCard
            isVisible={isModalVisible}
            handleClose={handleModalClose}
            selectedRowData={selectedRowData}
            source={source}
            editMode={selectedRowData?.approved === ApprovedStatus.Yes ? "ReadOnly" : "Edit"}
          />
          <DownloadModal
            isVisible={isDownloadModalVisible}
            handleClose={handleCloseDownloadModal}
            strSelectedJobs={selectedRowKeys}
            strJobDetails={selectedRecords}
            source={source}
          />
          <PrintModal
            isVisible={isPrintModalVisible}
            handleClose={handleClosePrintModal}
            strSelectedJobs={selectedRowKeys}
            source={source}
          />
          <HistoryModal
            isVisible={isHistoryModalVisible}
            jobId={selectedRowData?.jobId ?? 0}
            handleClose={handleCloseHistoryModal}
          />
          <NewFaxModal
            isVisible={isFaxModalVisible}
            onSubmit={handleFaxSubmit}
            handleClose={handleCloseFaxModal}
            jobIds={selectedRowKeys}
          />
        </>
      ) : (
        <>
          <Flex wrap gap="middle" justify="space-evenly">
            <DropdownFilter
              options={uniqueProviders}
              selectedOptions={selectedProviders}
              setSelectedOptions={setSelectedProviders}
              label="Provider"
            />
            <DropdownFilter
              options={uniqueReportTypes}
              selectedOptions={selectedReportTypes}
              setSelectedOptions={setSelectedReportTypes}
              label="Report Type"
            />
          </Flex>
          <Flex
            wrap="wrap"
            gap="middle"
            justify="space-between"
            style={{ marginTop: "1.75rem" }}>
            {(() => {
              if (isLoading) {
                return (
                  <div style={{ textAlign: "center", width: "100%" }}>
                    <Spin size="large" />
                  </div>
                );
              }

              if (filteredJobDetails.length === 0) {
                return (
                  <Typography.Title level={4} style={{ textAlign: "center", width: "100%" }}>
                    No Jobs Found
                  </Typography.Title>
                );
              }

              return filteredJobDetails.map(item => (
                <Card key={item.jobId} style={{ width: "100%", backgroundColor: "#FFF" }}>
                  <Flex
                    gap="middle"
                    align="end"
                    justify="space-between"
                    style={{ marginBottom: "16px" }}>
                    <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
                      {item.patientName}
                    </Typography.Title>
                    <Space size="small" direction="vertical" style={{ margin: 0 }}>
                      {getTag(item)}
                    </Space>
                  </Flex>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                      gap: "16px",
                      paddingTop: "16px"
                    }}>
                    <div style={{ display: "flex" }}>
                      <span className="font-777" style={{ textWrap: "nowrap" }}>
                        MRN:{" "}
                      </span>
                      <span
                        className="font-1212 font-1212-mobile"
                        style={{ textWrap: "nowrap" }}>
                        {item.jobId}
                      </span>
                    </div>
                    <div style={{ display: "flex" }}>
                      <span className="font-777" style={{ textWrap: "nowrap" }}>
                        Report Type:{" "}
                      </span>
                      <span
                        className="font-1212 font-1212-mobile"
                        style={{ textWrap: "nowrap" }}>
                        {item.reportType}
                      </span>
                    </div>
                    <div style={{ display: "flex" }}>
                      <span className="font-777" style={{ textWrap: "nowrap" }}>
                        Provider:{" "}
                      </span>
                      <span
                        className="font-1212 font-1212-mobile"
                        style={{ textWrap: "nowrap" }}>
                        {item.providerName}
                      </span>
                    </div>
                    <div style={{ display: "flex" }}>
                      <span className="font-777" style={{ textWrap: "nowrap" }}>
                        Date Of Job:{" "}
                      </span>
                      <span
                        className="font-1212 font-1212-mobile"
                        style={{ textWrap: "nowrap" }}>
                        {dayjs(item.dateOfDictation).format("MM/DD/YYYY")}
                      </span>
                    </div>
                    <div style={{ display: "flex" }}>
                      <span className="font-777" style={{ textWrap: "nowrap" }}>
                        Location:{" "}
                      </span>
                      <span
                        className="font-1212 font-1212-mobile"
                        style={{ textWrap: "nowrap" }}>
                        {item.locationName}
                      </span>
                    </div>
                    <div style={{ display: "flex" }}>
                      <span className="font-777" style={{ textWrap: "nowrap" }}>
                        Date Of Service:{" "}
                      </span>
                      <span
                        className="font-1212 font-1212-mobile"
                        style={{ textWrap: "nowrap" }}>
                        {dayjs(item.visitDate).format("MM/DD/YYYY")}
                      </span>
                    </div>
                  </div>
                </Card>
              ));
            })()}
          </Flex>
        </>
      )}
    </>
  );
};

export default JobGrid;
